import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { tasksAPI, labelsAPI, teamsAPI, aiAPI, exportAPI, usersAPI, timeAPI, quotesAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Trash2,
  Archive,
  ArchiveRestore,
  Edit,
  Calendar as CalendarIcon,
  Download,
  Sparkles,
  Package,
  Paperclip,
  X,
  Eye,
  FileText,
  Image as ImageIcon,
  CheckCircle,
  Clock,
  AlertTriangle,
  Circle,
  Wrench,
  Lightbulb,
  ListChecks,
  PlusCircle,
  User as UserIcon,
  Camera,
  Receipt,
  Copy,
  Play,
  Square,
  Timer
} from 'lucide-react';

import PhotoJobWizard from '../components/PhotoJobWizard';

const STATUSES = ['OPEN', 'IN_PROGRESS', 'WAITING_ON_PARTS', 'BLOCKED', 'DONE'];

const statusLabels = {
  OPEN: 'OPEN',
  IN_PROGRESS: 'IN PROGRESS',
  WAITING_ON_PARTS: 'WAITING ON PARTS',
  BLOCKED: 'BLOCKED',
  DONE: 'DONE'
};

const statusIcons = {
  OPEN: Circle,
  IN_PROGRESS: Clock,
  WAITING_ON_PARTS: Package,
  BLOCKED: AlertTriangle,
  DONE: CheckCircle
};

const statusColors = {
  OPEN: 'bg-blue-100 text-blue-800 border-blue-200',
  IN_PROGRESS: 'bg-amber-100 text-amber-800 border-amber-200',
  WAITING_ON_PARTS: 'bg-purple-100 text-purple-800 border-purple-200',
  BLOCKED: 'bg-red-100 text-red-800 border-red-200',
  DONE: 'bg-emerald-100 text-emerald-800 border-emerald-200'
};

const TasksPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [tasks, setTasks] = useState([]);
  const [labels, setLabels] = useState([]);
  const [groups, setGroups] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [labelFilter, setLabelFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  
  // Task form
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    status: 'OPEN',
    estimate: '',
    due_date: null,
    assignee_id: '',
    assignee_email: '',
    group_id: '',
    labels: []
  });
  
  // Task detail
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  
  // Bulk selection
  // Task selection for ordering
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [orderMode, setOrderMode] = useState(false);
  
  // AI suggestions
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [parsedAiSuggestions, setParsedAiSuggestions] = useState([]);
  
  // Parts form
  const [showPartDialog, setShowPartDialog] = useState(false);
  const [partForm, setPartForm] = useState({ name: '', quantity: 1, supplier: '' });
  
  // Tools form
  const [showToolDialog, setShowToolDialog] = useState(false);
  const [toolName, setToolName] = useState('');
  
  // Image preview
  const [previewImage, setPreviewImage] = useState(null);
  
  // Delete confirmation
  const [deleteTaskId, setDeleteTaskId] = useState(null);
  
  // Photo wizard
  const [showPhotoWizard, setShowPhotoWizard] = useState(false);
  
  // Quote in AI Assist
  const [quoteData, setQuoteData] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  // Quote save/edit
  const [showQuoteEditor, setShowQuoteEditor] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ title: '', summary: '', notes: '', line_items: [] });
  const [taskQuotes, setTaskQuotes] = useState([]);

  // Time tracking
  const [activeTimeEntry, setActiveTimeEntry] = useState(null);
  const [taskTimeEntries, setTaskTimeEntries] = useState(null);
  const [manualTimeForm, setManualTimeForm] = useState({ duration_minutes: '', note: '', date: '' });
  const [showManualTimeDialog, setShowManualTimeDialog] = useState(false);

  const fetchTasks = useCallback(async () => {
    try {
      const params = {
        archived: showArchived,
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(groupFilter && { group_id: groupFilter }),
        ...(labelFilter && { label_id: labelFilter }),
      };
      const response = await tasksAPI.list(params);
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, groupFilter, labelFilter, showArchived]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [labelsRes, groupsRes, usersRes] = await Promise.all([
          labelsAPI.list(),
          teamsAPI.list(),
          usersAPI.assignable()
        ]);
        setLabels(labelsRes.data);
        setGroups(groupsRes.data);
        setAssignableUsers(usersRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    const taskId = searchParams.get('task');
    if (taskId) {
      openTaskDetail(taskId);
    }
  }, [searchParams]);

  const openTaskDetail = async (taskId) => {
    try {
      const response = await tasksAPI.get(taskId);
      setSelectedTask(response.data);
      setShowTaskDetail(true);
      fetchTaskTimeEntries(taskId);
      fetchTaskQuotes(taskId);
    } catch (error) {
      toast.error('Task not found');
    }
  };

  // Time tracking functions
  const fetchActiveTimeEntry = async () => {
    try {
      const res = await timeAPI.getActive();
      setActiveTimeEntry(res.data);
    } catch { setActiveTimeEntry(null); }
  };

  const fetchTaskTimeEntries = async (taskId) => {
    try {
      const res = await timeAPI.getTaskEntries(taskId);
      setTaskTimeEntries(res.data);
    } catch { setTaskTimeEntries(null); }
  };

  const handleCheckIn = async (taskId) => {
    try {
      await timeAPI.checkIn(taskId);
      toast.success('Checked in');
      fetchActiveTimeEntry();
      fetchTaskTimeEntries(taskId);
      window.dispatchEvent(new Event('timetracking-changed'));
    } catch (error) {
      toast.error('Failed to check in');
    }
  };

  const handleCheckOut = async () => {
    try {
      await timeAPI.checkOut();
      toast.success('Checked out');
      setActiveTimeEntry(null);
      if (selectedTask) fetchTaskTimeEntries(selectedTask.task_id);
      window.dispatchEvent(new Event('timetracking-changed'));
    } catch (error) {
      toast.error('Failed to check out');
    }
  };

  const handleAddManualTime = async () => {
    if (!manualTimeForm.duration_minutes || !selectedTask) return;
    try {
      await timeAPI.addManual(selectedTask.task_id, {
        duration_minutes: parseFloat(manualTimeForm.duration_minutes),
        note: manualTimeForm.note,
        date: manualTimeForm.date ? new Date(manualTimeForm.date + 'T00:00:00').toISOString() : new Date().toISOString(),
      });
      toast.success('Time entry added');
      setShowManualTimeDialog(false);
      setManualTimeForm({ duration_minutes: '', note: '', date: '' });
      fetchTaskTimeEntries(selectedTask.task_id);
    } catch (error) {
      toast.error('Failed to add time entry');
    }
  };

  const handleDeleteTimeEntry = async (entryId) => {
    try {
      await timeAPI.deleteEntry(entryId);
      toast.success('Time entry deleted');
      if (selectedTask) fetchTaskTimeEntries(selectedTask.task_id);
    } catch (error) {
      toast.error('Failed to delete time entry');
    }
  };

  // Fetch active time entry on mount
  useEffect(() => { fetchActiveTimeEntry(); }, []);

  const handleCreateTask = () => {
    setEditingTask(null);
    setTaskForm({
      title: '',
      description: '',
      status: 'OPEN',
      estimate: '',
      due_date: null,
      assignee_id: '',
      assignee_email: '',
      group_id: '',
      labels: []
    });
    setShowTaskDialog(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setTaskForm({
      title: task.title,
      description: task.description || '',
      status: task.status,
      estimate: task.estimate || '',
      due_date: task.due_date ? new Date(task.due_date) : null,
      assignee_id: task.assignee_id || '',
      assignee_email: task.assignee_email || '',
      group_id: task.group_id || '',
      labels: task.labels || []
    });
    setShowTaskDialog(true);
  };

  const handleSaveTask = async () => {
    if (!taskForm.title.trim()) {
      toast.error('Title is required');
      return;
    }

    try {
      const data = {
        ...taskForm,
        estimate: taskForm.estimate ? parseFloat(taskForm.estimate) : null,
        due_date: taskForm.due_date?.toISOString() || null,
        assignee_id: taskForm.assignee_id || null,
        assignee_email: taskForm.assignee_email || null,
        group_id: taskForm.group_id || null,
      };

      if (editingTask) {
        await tasksAPI.update(editingTask.task_id, data);
        toast.success('Task updated');
        // Refresh the task detail if it's open
        if (selectedTask?.task_id === editingTask.task_id) {
          const updated = await tasksAPI.get(editingTask.task_id);
          setSelectedTask(updated.data);
        }
      } else {
        await tasksAPI.create(data);
        toast.success('Task created');
      }

      setShowTaskDialog(false);
      fetchTasks();
    } catch (error) {
      toast.error('Failed to save task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await tasksAPI.delete(taskId);
      toast.success('Task deleted');
      setDeleteTaskId(null);
      fetchTasks();
      if (selectedTask?.task_id === taskId) {
        setShowTaskDetail(false);
        setSelectedTask(null);
      }
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleArchiveTask = async (taskId, archived) => {
    try {
      if (archived) {
        await tasksAPI.unarchive(taskId);
        toast.success('Task unarchived');
      } else {
        await tasksAPI.archive(taskId);
        toast.success('Task archived');
      }
      fetchTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await tasksAPI.update(taskId, { status: newStatus });
      toast.success('Status updated');
      fetchTasks();
      if (selectedTask?.task_id === taskId) {
        setSelectedTask({ ...selectedTask, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAddPart = async () => {
    if (!partForm.name.trim()) {
      toast.error('Part name is required');
      return;
    }

    try {
      await tasksAPI.addPart(selectedTask.task_id, partForm);
      toast.success('Part added');
      setShowPartDialog(false);
      setPartForm({ name: '', quantity: 1, supplier: '' });
      const updated = await tasksAPI.get(selectedTask.task_id);
      setSelectedTask(updated.data);
    } catch (error) {
      toast.error('Failed to add part');
    }
  };

  const handleUpdatePart = async (partId, updates) => {
    try {
      await tasksAPI.updatePart(selectedTask.task_id, partId, updates);
      const updated = await tasksAPI.get(selectedTask.task_id);
      setSelectedTask(updated.data);
      toast.success('Part updated');
    } catch (error) {
      toast.error('Failed to update part');
    }
  };

  const handleDeletePart = async (partId) => {
    try {
      await tasksAPI.deletePart(selectedTask.task_id, partId);
      const updated = await tasksAPI.get(selectedTask.task_id);
      setSelectedTask(updated.data);
      toast.success('Part deleted');
    } catch (error) {
      toast.error('Failed to delete part');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      await tasksAPI.uploadAttachment(selectedTask.task_id, file);
      toast.success('File uploaded');
      const updated = await tasksAPI.get(selectedTask.task_id);
      setSelectedTask(updated.data);
    } catch (error) {
      toast.error('Failed to upload file');
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await tasksAPI.deleteAttachment(selectedTask.task_id, attachmentId);
      const updated = await tasksAPI.get(selectedTask.task_id);
      setSelectedTask(updated.data);
      toast.success('Attachment deleted');
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const handleSignOff = async () => {
    const clientId = window.prompt('Enter client ID for sign-off:');
    if (!clientId) return;
    const note = window.prompt('Add a note (optional):');
    
    try {
      await tasksAPI.addSignOff(selectedTask.task_id, { client_id: clientId, note });
      const updated = await tasksAPI.get(selectedTask.task_id);
      setSelectedTask(updated.data);
      toast.success('Sign-off added');
    } catch (error) {
      toast.error('Failed to add sign-off');
    }
  };

  const handleGetAISuggestion = async (type) => {
    setAiLoading(true);
    setAiSuggestions(null);
    setParsedAiSuggestions([]);
    try {
      const response = await aiAPI.suggest({
        task_title: selectedTask.title,
        task_description: selectedTask.description,
        suggestion_type: type,
        label_ids: selectedTask.labels || []
      });
      const rawResult = response.data.result;
      setAiSuggestions({ type, result: rawResult });
      
      // Try to parse JSON from the result
      try {
        // Extract JSON array from the response (handle markdown code blocks)
        let jsonStr = rawResult;
        const jsonMatch = rawResult.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          jsonStr = jsonMatch[0];
        }
        let parsed = JSON.parse(jsonStr);
        parsed = Array.isArray(parsed) ? parsed : [];
        
        // Filter out already existing items
        if (type === 'tools' && selectedTask.tools?.length > 0) {
          const existingToolNames = selectedTask.tools.map(t => t.name.toLowerCase());
          parsed = parsed.filter(tool => {
            const toolName = (typeof tool === 'string' ? tool : tool.name).toLowerCase();
            return !existingToolNames.includes(toolName);
          });
        } else if (type === 'parts' && selectedTask.parts?.length > 0) {
          const existingPartNames = selectedTask.parts.map(p => p.name.toLowerCase());
          parsed = parsed.filter(part => {
            return !existingPartNames.includes(part.name.toLowerCase());
          });
        }
        
        setParsedAiSuggestions(parsed);
      } catch (parseErr) {
        console.log('Could not parse AI response as JSON:', parseErr);
        setParsedAiSuggestions([]);
      }
    } catch (error) {
      toast.error('Failed to get AI suggestion');
    } finally {
      setAiLoading(false);
    }
  };

  // Add AI-suggested part to task
  const handleAddAIPart = async (part) => {
    try {
      await tasksAPI.addPart(selectedTask.task_id, {
        name: part.name,
        quantity: part.quantity || 1,
        supplier: part.supplier || '',
        unit_price: part.unit_price || null
      });
      toast.success(`Added part: ${part.name}`);
      const updated = await tasksAPI.get(selectedTask.task_id);
      setSelectedTask(updated.data);
      // Remove the added part from suggestions
      setParsedAiSuggestions(prev => prev.filter(p => 
        p.name.toLowerCase() !== part.name.toLowerCase()
      ));
    } catch (error) {
      toast.error('Failed to add part');
    }
  };

  // Add AI-suggested tool to task
  const handleAddAITool = async (toolName) => {
    try {
      await tasksAPI.addTool(selectedTask.task_id, { name: toolName });
      toast.success(`Added tool: ${toolName}`);
      const updated = await tasksAPI.get(selectedTask.task_id);
      setSelectedTask(updated.data);
      // Remove the added tool from suggestions
      setParsedAiSuggestions(prev => prev.filter(tool => {
        const name = typeof tool === 'string' ? tool : tool.name;
        return name.toLowerCase() !== toolName.toLowerCase();
      }));
    } catch (error) {
      toast.error('Failed to add tool');
    }
  };

  // Add breakdown to description
  const handleAddBreakdownToDescription = async () => {
    if (!parsedAiSuggestions.length) return;
    
    const breakdownText = parsedAiSuggestions.map((item, idx) => 
      `${idx + 1}. ${typeof item === 'string' ? item : item.title || item.name || JSON.stringify(item)}`
    ).join('\n');
    
    const newDescription = selectedTask.description 
      ? `${selectedTask.description}\n\n## Task Breakdown\n${breakdownText}`
      : `## Task Breakdown\n${breakdownText}`;
    
    try {
      await tasksAPI.update(selectedTask.task_id, { description: newDescription });
      toast.success('Breakdown added to description');
      const updated = await tasksAPI.get(selectedTask.task_id);
      setSelectedTask(updated.data);
      setAiSuggestions(null);
      setParsedAiSuggestions([]);
    } catch (error) {
      toast.error('Failed to update description');
    }
  };

  // Add tips to description
  const handleAddTipsToDescription = async () => {
    if (!parsedAiSuggestions.length) return;
    
    const tipsText = parsedAiSuggestions.map((tip, idx) => 
      `• ${typeof tip === 'string' ? tip : JSON.stringify(tip)}`
    ).join('\n');
    
    const newDescription = selectedTask.description 
      ? `${selectedTask.description}\n\n## Tips & Tricks\n${tipsText}`
      : `## Tips & Tricks\n${tipsText}`;
    
    try {
      await tasksAPI.update(selectedTask.task_id, { description: newDescription });
      toast.success('Tips added to description');
      const updated = await tasksAPI.get(selectedTask.task_id);
      setSelectedTask(updated.data);
      setAiSuggestions(null);
      setParsedAiSuggestions([]);
    } catch (error) {
      toast.error('Failed to update description');
    }
  };

  // Request a quote for the current task
  const handleRequestQuote = async () => {
    if (!selectedTask) return;
    setQuoteLoading(true);
    setQuoteData(null);
    try {
      const res = await aiAPI.generateQuote({
        title: selectedTask.title,
        description: selectedTask.description,
        parts: (selectedTask.parts || []).map(p => ({ name: p.name, quantity: p.quantity, supplier: p.supplier, unit_price: p.unit_price })),
        tools: (selectedTask.tools || []).map(t => t.name),
        estimate_hours: selectedTask.estimate || 0,
      });
      const raw = res.data.quote;
      let parsed = typeof raw === 'string' ? JSON.parse(raw.replace(/```json\n?|```/g, '').trim()) : raw;
      setQuoteData(parsed);
    } catch (error) {
      toast.error('Failed to generate quote');
    } finally {
      setQuoteLoading(false);
    }
  };

  const copyQuote = () => {
    if (!quoteData || !selectedTask) return;
    const text = `${selectedTask.title}\n\n${quoteData.summary}\n\nMaterials: ${quoteData.materials_estimate}\nLabour (${quoteData.labour_hours}hrs): ${quoteData.labour_estimate}\nTotal Estimate: ${quoteData.total_estimate}\n\n${quoteData.notes || ''}`;
    navigator.clipboard.writeText(text);
    toast.success('Quote copied to clipboard');
  };

  // Open quote editor with AI-generated data pre-filled
  // Extract first dollar amount from a string like "$14,500 - $16,500" → 14500
  const parseFirstAmount = (str) => {
    if (!str) return 0;
    const match = str.match(/\$?([\d,]+(?:\.\d+)?)/);
    if (match) return parseFloat(match[1].replace(/,/g, '')) || 0;
    return 0;
  };

  const openQuoteEditor = () => {
    if (!quoteData || !selectedTask) return;
    const lineItems = [];
    if (quoteData.materials_estimate) {
      lineItems.push({ description: 'Materials', quantity: 1, unit_price: parseFirstAmount(quoteData.materials_estimate) });
    }
    if (quoteData.labour_hours && quoteData.labour_estimate) {
      const labourTotal = parseFirstAmount(quoteData.labour_estimate);
      const hours = parseFloat(quoteData.labour_hours) || 1;
      lineItems.push({ description: `Labour (${quoteData.labour_hours} hrs)`, quantity: hours, unit_price: Math.round((labourTotal / hours) * 100) / 100 });
    }
    setQuoteForm({
      title: `Quote — ${selectedTask.title}`,
      summary: quoteData.summary || '',
      notes: quoteData.notes || '',
      line_items: lineItems,
    });
    setShowQuoteEditor(true);
  };

  const handleSaveQuote = async () => {
    if (!selectedTask) return;
    try {
      await quotesAPI.create({
        task_id: selectedTask.task_id,
        title: quoteForm.title,
        summary: quoteForm.summary,
        notes: quoteForm.notes,
        line_items: quoteForm.line_items.map(li => ({
          description: li.description,
          quantity: parseFloat(li.quantity) || 0,
          unit_price: parseFloat(li.unit_price) || 0,
        })),
      });
      toast.success('Quote saved');
      setShowQuoteEditor(false);
      fetchTaskQuotes(selectedTask.task_id);
    } catch { toast.error('Failed to save quote'); }
  };

  const fetchTaskQuotes = async (taskId) => {
    try {
      const res = await quotesAPI.list({ task_id: taskId });
      setTaskQuotes(res.data);
    } catch { setTaskQuotes([]); }
  };

  const updateQuoteFormItem = (idx, field, value) => {
    const items = [...quoteForm.line_items];
    items[idx] = { ...items[idx], [field]: value };
    setQuoteForm({ ...quoteForm, line_items: items });
  };

  const quoteFormSubtotal = quoteForm.line_items.reduce((s, li) => s + (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0), 0);

  // Add tool manually
  const handleAddTool = async () => {
    if (!toolName.trim()) {
      toast.error('Tool name is required');
      return;
    }
    try {
      await tasksAPI.addTool(selectedTask.task_id, { name: toolName });
      toast.success('Tool added');
      setShowToolDialog(false);
      setToolName('');
      const updated = await tasksAPI.get(selectedTask.task_id);
      setSelectedTask(updated.data);
    } catch (error) {
      toast.error('Failed to add tool');
    }
  };

  // Delete tool
  const handleDeleteTool = async (toolId) => {
    try {
      await tasksAPI.deleteTool(selectedTask.task_id, toolId);
      const updated = await tasksAPI.get(selectedTask.task_id);
      setSelectedTask(updated.data);
      toast.success('Tool deleted');
    } catch (error) {
      toast.error('Failed to delete tool');
    }
  };

  const toggleTaskSelection = (taskId) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const isImageFile = (filetype) => filetype?.startsWith('image/');

  const getLabelById = (labelId) => labels.find(l => l.label_id === labelId);
  
  const getFirstImageAttachment = (task) => task.attachments?.find(a => a.filetype?.startsWith('image/'));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-heading">Tasks</h1>
          <p className="text-slate-600">Manage and track your project tasks</p>
        </div>
        <div className="flex items-center gap-2">
          {orderMode ? (
            <>
              <Button 
                variant="outline"
                onClick={() => { setOrderMode(false); setSelectedTasks([]); }}
                data-testid="cancel-order-mode-btn"
              >
                Cancel
              </Button>
              <Button 
                onClick={() => navigate('/dashboard/orders', { state: { selectedTasks } })}
                disabled={selectedTasks.length === 0}
                data-testid="prepare-order-btn"
              >
                <Package className="mr-2 h-4 w-4" />
                Order Parts ({selectedTasks.length})
              </Button>
            </>
          ) : (
            <Button 
              variant="outline"
              onClick={() => setOrderMode(true)}
              data-testid="start-order-mode-btn"
            >
              <Package className="mr-2 h-4 w-4" />
              Prepare Parts Order
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem asChild>
                <a href={exportAPI.tasks('csv')} target="_blank" rel="noopener noreferrer">
                  Export as CSV
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href={exportAPI.tasks('xlsx')} target="_blank" rel="noopener noreferrer">
                  Export as Excel
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={handleCreateTask} data-testid="create-task-btn">
            <Plus className="mr-2 h-4 w-4" />
            New Task
          </Button>
          <Button className="bg-amber-500 hover:bg-amber-600 text-white shadow-md" onClick={() => setShowPhotoWizard(true)} data-testid="photo-job-btn">
            <Camera className="mr-2 h-5 w-5" />
            New Job from Photo
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search tasks..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter || "all"} onValueChange={(val) => setStatusFilter(val === "all" ? "" : val)}>
              <SelectTrigger className="w-full lg:w-40" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{statusLabels[s] || s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={groupFilter || "all"} onValueChange={(val) => setGroupFilter(val === "all" ? "" : val)}>
              <SelectTrigger className="w-full lg:w-40" data-testid="team-filter">
                <SelectValue placeholder="Team" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teams</SelectItem>
                {groups.map(g => (
                  <SelectItem key={g.group_id} value={g.group_id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={labelFilter || "all"} onValueChange={(val) => setLabelFilter(val === "all" ? "" : val)}>
              <SelectTrigger className="w-full lg:w-40" data-testid="label-filter">
                <SelectValue placeholder="Label" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Labels</SelectItem>
                {labels.map(l => (
                  <SelectItem key={l.label_id} value={l.label_id}>
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: l.color }} />
                      {l.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="show-archived" 
                checked={showArchived}
                onCheckedChange={setShowArchived}
              />
              <Label htmlFor="show-archived" className="text-sm">Show archived</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tasks List */}
      <Card>
        <CardContent className="p-0">
          {tasks.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-slate-400" />
              </div>
              <h3 className="font-medium text-slate-900">No tasks found</h3>
              <p className="text-slate-600 mt-1">Create your first task to get started</p>
              <Button onClick={handleCreateTask} className="mt-4">
                <Plus className="mr-2 h-4 w-4" />
                Create Task
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {tasks.map((task) => {
                const StatusIcon = statusIcons[task.status] || Circle;
                return (
                  <div
                    key={task.task_id}
                    className="flex flex-col sm:flex-row sm:items-center p-4 hover:bg-slate-50 transition-colors group"
                    data-testid={`task-row-${task.task_id}`}
                  >
                    <div className="flex items-center flex-1 min-w-0">
                    {orderMode && (
                      <Checkbox
                        checked={selectedTasks.includes(task.task_id)}
                        onCheckedChange={() => toggleTaskSelection(task.task_id)}
                        className="mr-4"
                      />
                    )}
                    {getFirstImageAttachment(task) && (
                      <div 
                        className="w-11 h-11 rounded-lg overflow-hidden flex-shrink-0 border border-slate-200 cursor-pointer mr-3 bg-slate-100"
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          setPreviewImage(tasksAPI.getAttachmentUrl(task.task_id, getFirstImageAttachment(task).attachment_id)); 
                        }}
                        data-testid={`task-thumbnail-${task.task_id}`}
                      >
                        <img 
                          src={tasksAPI.getAttachmentUrl(task.task_id, getFirstImageAttachment(task).attachment_id)} 
                          alt="" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div 
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => openTaskDetail(task.task_id)}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className={`font-medium ${task.archived ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                          {task.title}
                        </h3>
                        {task.archived && (
                          <Badge variant="secondary" className="text-xs">Archived</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-slate-500">
                        {task.assignee_name && (
                          <span className="flex items-center gap-1 text-slate-700 font-medium">
                            <UserIcon className="h-3 w-3" />
                            {task.assignee_name}
                          </span>
                        )}
                        {task.due_date && (
                          <span className="flex items-center gap-1">
                            <CalendarIcon className="h-3 w-3" />
                            {format(new Date(task.due_date), 'MMM d')}
                          </span>
                        )}
                        {task.estimate && (
                          <span>{task.estimate}h</span>
                        )}
                        {task.parts?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Package className="h-3 w-3" />
                            {task.parts.length}
                          </span>
                        )}
                        {task.attachments?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Paperclip className="h-3 w-3" />
                            {task.attachments.length}
                          </span>
                        )}
                      </div>
                      {task.label_details?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {task.label_details.map(label => (
                            <Badge 
                              key={label.label_id}
                              style={{ backgroundColor: label.color + '20', color: label.color }}
                              className="text-xs"
                            >
                              {label.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 mt-2 sm:mt-0 pl-0 sm:pl-0">
                      {/* Quick check-in/out button */}
                      {activeTimeEntry && activeTimeEntry.task_id === task.task_id ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 px-2 sm:px-3 border-green-300 bg-green-50 hover:bg-red-50 hover:border-red-300 text-green-700 hover:text-red-700 flex-shrink-0"
                          onClick={(e) => { e.stopPropagation(); handleCheckOut(); }}
                          data-testid={`task-checkout-${task.task_id}`}
                        >
                          <Square className="h-3.5 w-3.5 mr-1.5" />
                          <span className="text-xs">Stop</span>
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 sm:px-3 text-slate-400 hover:text-green-700 hover:bg-green-50 flex-shrink-0"
                          onClick={(e) => { e.stopPropagation(); handleCheckIn(task.task_id); }}
                          data-testid={`task-checkin-${task.task_id}`}
                        >
                          <Play className="h-3.5 w-3.5 mr-1.5" />
                          <span className="text-xs">Clock In</span>
                        </Button>
                      )}
                      <Select 
                        value={task.status} 
                        onValueChange={(val) => handleStatusChange(task.task_id, val)}
                      >
                        <SelectTrigger className={`w-auto sm:w-36 h-8 text-xs ${statusColors[task.status]}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map(s => {
                            const Icon = statusIcons[s];
                            return (
                              <SelectItem key={s} value={s}>
                                <span className="flex items-center gap-2">
                                  <Icon className="h-3 w-3" />
                                  {statusLabels[s] || s}
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditTask(task)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleArchiveTask(task.task_id, task.archived)}>
                            {task.archived ? (
                              <>
                                <ArchiveRestore className="mr-2 h-4 w-4" />
                                Unarchive
                              </>
                            ) : (
                              <>
                                <Archive className="mr-2 h-4 w-4" />
                                Archive
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => setDeleteTaskId(task.task_id)}
                            className="text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingTask ? 'Edit Task' : 'Create Task'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                placeholder="Task title"
                data-testid="task-title-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                placeholder="Task description"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Status</Label>
                <Select 
                  value={taskForm.status} 
                  onValueChange={(val) => setTaskForm({ ...taskForm, status: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => (
                      <SelectItem key={s} value={s}>{statusLabels[s] || s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Estimate (hours)</Label>
                <Input
                  type="number"
                  value={taskForm.estimate}
                  onChange={(e) => setTaskForm({ ...taskForm, estimate: e.target.value })}
                  placeholder="0"
                  min="0"
                  step="0.5"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Due Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {taskForm.due_date ? format(taskForm.due_date, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={taskForm.due_date}
                      onSelect={(date) => setTaskForm({ ...taskForm, due_date: date })}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Team</Label>
                <Select 
                  value={taskForm.group_id || "none"} 
                  onValueChange={(val) => setTaskForm({ ...taskForm, group_id: val === "none" ? "" : val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select team" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No team</SelectItem>
                    {groups.map(g => (
                      <SelectItem key={g.group_id} value={g.group_id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Assignee</Label>
              <Select 
                value={taskForm.assignee_id || "unassigned"} 
                onValueChange={(val) => setTaskForm({ ...taskForm, assignee_id: val === "unassigned" ? "" : val, assignee_email: "" })}
              >
                <SelectTrigger data-testid="assignee-select">
                  <SelectValue placeholder="Select assignee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {assignableUsers.map(u => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.name} ({u.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Labels</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {labels.map(label => (
                  <Badge
                    key={label.label_id}
                    variant={taskForm.labels.includes(label.label_id) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    style={taskForm.labels.includes(label.label_id) 
                      ? { backgroundColor: label.color, borderColor: label.color }
                      : { borderColor: label.color, color: label.color }
                    }
                    onClick={() => {
                      const newLabels = taskForm.labels.includes(label.label_id)
                        ? taskForm.labels.filter(id => id !== label.label_id)
                        : [...taskForm.labels, label.label_id];
                      setTaskForm({ ...taskForm, labels: newLabels });
                    }}
                  >
                    {label.name}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTask} data-testid="save-task-btn">
              {editingTask ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Detail Dialog */}
      <Dialog open={showTaskDetail} onOpenChange={(open) => {
        setShowTaskDetail(open);
        if (!open) {
          setSelectedTask(null);
          setQuoteData(null);
          setSearchParams({});
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
          {selectedTask && (
            <>
              <DialogHeader className="flex-shrink-0">
                <div className="flex items-center justify-between">
                  <DialogTitle className="font-heading text-xl pr-8">
                    {selectedTask.title}
                  </DialogTitle>
                  <Badge className={statusColors[selectedTask.status]}>
                    {statusLabels[selectedTask.status] || selectedTask.status}
                  </Badge>
                </div>
                <DialogDescription className="sr-only">
                  Task details for {selectedTask.title}
                </DialogDescription>
              </DialogHeader>
              
              <div className="flex-1 overflow-y-auto -mx-6 px-6 min-h-0">
                <Tabs defaultValue="details" className="w-full">
                  <TabsList className="mb-4 flex-wrap">
                    <TabsTrigger value="details">Details</TabsTrigger>
                    <TabsTrigger value="parts">Parts ({selectedTask.parts?.length || 0})</TabsTrigger>
                    <TabsTrigger value="tools">Tools ({selectedTask.tools?.length || 0})</TabsTrigger>
                    <TabsTrigger value="attachments">Files ({selectedTask.attachments?.length || 0})</TabsTrigger>
                    <TabsTrigger value="ai">AI Assist</TabsTrigger>
                    <TabsTrigger value="time" data-testid="task-time-tab">Time</TabsTrigger>
                    <TabsTrigger value="quotes" data-testid="task-quotes-tab">Quotes ({taskQuotes.length})</TabsTrigger>
                    <TabsTrigger value="activity">Activity</TabsTrigger>
                  </TabsList>

                  <TabsContent value="details" className="space-y-4">
                    {/* Job Photo */}
                    {getFirstImageAttachment(selectedTask) && (
                      <div>
                        <Label className="text-slate-500">Job Photo</Label>
                        <div 
                          className="mt-2 rounded-lg overflow-hidden border border-slate-200 cursor-pointer inline-block"
                          onClick={() => setPreviewImage(tasksAPI.getAttachmentUrl(selectedTask.task_id, getFirstImageAttachment(selectedTask).attachment_id))}
                          data-testid="task-detail-photo"
                        >
                          <img 
                            src={tasksAPI.getAttachmentUrl(selectedTask.task_id, getFirstImageAttachment(selectedTask).attachment_id)} 
                            alt="Job photo" 
                            className="max-w-full max-h-48 object-contain"
                          />
                        </div>
                      </div>
                    )}
                    {/* Description Section */}
                    <div>
                      <Label className="text-slate-500">Description</Label>
                      <div className="mt-2 p-4 bg-slate-50 rounded-lg min-h-[80px]">
                        {selectedTask.description ? (
                          <p className="text-slate-700 whitespace-pre-wrap">{selectedTask.description}</p>
                        ) : (
                          <p className="text-slate-400 italic">No description provided</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-500">Status</Label>
                        <Select 
                          value={selectedTask.status}
                          onValueChange={(val) => handleStatusChange(selectedTask.task_id, val)}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map(s => (
                              <SelectItem key={s} value={s}>{statusLabels[s] || s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-500">Estimate</Label>
                        <p className="mt-1 font-medium">{selectedTask.estimate ? `${selectedTask.estimate} hours` : 'Not set'}</p>
                      </div>
                      <div>
                        <Label className="text-slate-500">Due Date</Label>
                        <p className="mt-1 font-medium">
                          {selectedTask.due_date ? format(new Date(selectedTask.due_date), 'PPP') : 'Not set'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-slate-500">Assignee</Label>
                        <Select 
                          value={selectedTask.assignee_id || "unassigned"}
                          onValueChange={async (val) => {
                            const newAssigneeId = val === "unassigned" ? null : val;
                            try {
                              await tasksAPI.update(selectedTask.task_id, { assignee_id: newAssigneeId, assignee_email: null });
                              const updated = await tasksAPI.get(selectedTask.task_id);
                              setSelectedTask(updated.data);
                              fetchTasks();
                              toast.success('Assignee updated');
                            } catch (error) {
                              toast.error('Failed to update assignee');
                            }
                          }}
                        >
                          <SelectTrigger className="mt-1" data-testid="detail-assignee-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unassigned">Unassigned</SelectItem>
                            {assignableUsers.map(u => (
                              <SelectItem key={u.user_id} value={u.user_id}>
                                {u.name} ({u.email})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {selectedTask.label_details?.length > 0 && (
                      <div>
                        <Label className="text-slate-500">Labels</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {selectedTask.label_details.map(label => (
                            <Badge
                              key={label.label_id}
                              style={{ backgroundColor: label.color + '20', color: label.color }}
                            >
                              {label.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedTask.sign_off ? (
                      <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                        <div className="flex items-center gap-2 text-emerald-800">
                          <CheckCircle className="h-5 w-5" />
                          <span className="font-medium">Signed off by {selectedTask.sign_off.client_id}</span>
                        </div>
                        {selectedTask.sign_off.note && (
                          <p className="text-emerald-700 mt-1">{selectedTask.sign_off.note}</p>
                        )}
                        <p className="text-sm text-emerald-600 mt-1">
                          {format(new Date(selectedTask.sign_off.signed_at), 'PPP')}
                        </p>
                      </div>
                    ) : (
                      <Button variant="outline" onClick={handleSignOff}>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Request Sign-off
                      </Button>
                    )}
                  </TabsContent>

                  <TabsContent value="parts" className="space-y-4">
                    <div className="flex justify-between items-center gap-2 flex-wrap">
                      <h3 className="font-medium">Parts List</h3>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleGetAISuggestion('parts')}
                          disabled={aiLoading}
                        >
                          <Sparkles className="mr-2 h-4 w-4" />
                          AI Suggest
                        </Button>
                        <Button size="sm" onClick={() => setShowPartDialog(true)}>
                          <Plus className="mr-2 h-4 w-4" />
                          Add Part
                        </Button>
                      </div>
                    </div>
                    
                    {/* AI Parts Suggestions inline */}
                    {aiSuggestions?.type === 'parts' && parsedAiSuggestions.length > 0 && (
                      <Card className="border-indigo-200 bg-indigo-50/50">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <Sparkles className="h-4 w-4 text-indigo-500" />
                              AI Suggested Parts
                            </CardTitle>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setAiSuggestions(null);
                                setParsedAiSuggestions([]);
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {parsedAiSuggestions.map((part, idx) => (
                            <div 
                              key={idx}
                              className="flex items-center justify-between p-2 bg-white rounded-lg"
                            >
                              <div>
                                <span className="font-medium">{part.name}</span>
                                <span className="text-slate-500 ml-2 text-sm">
                                  Qty: {part.quantity || 1}
                                  {part.supplier && ` · ${part.supplier}`}
                                  {part.unit_price != null && ` · $${Number(part.unit_price).toFixed(2)}`}
                                </span>
                              </div>
                              <Button 
                                size="sm" 
                                onClick={() => handleAddAIPart(part)}
                              >
                                <PlusCircle className="mr-1 h-4 w-4" />
                                Add
                              </Button>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    )}
                    
                    {selectedTask.parts?.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Package className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                        <p>No parts added yet</p>
                        <p className="text-sm mt-1">Use "AI Suggest" to get recommendations</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {selectedTask.parts?.map(part => (
                          <div 
                            key={part.part_id}
                            className={`flex items-center justify-between p-3 border rounded-lg ${part.ordered ? 'bg-emerald-50 border-emerald-200' : ''}`}
                          >
                            <div className="flex items-center gap-3">
                              <div>
                                <span className={`font-medium ${part.ordered ? 'text-emerald-700' : ''}`}>
                                  {part.name}
                                  {part.ordered && <Badge className="ml-2 bg-emerald-100 text-emerald-700 text-xs">Ordered</Badge>}
                                </span>
                                <p className="text-sm text-slate-500">
                                  Qty: {part.quantity} {part.supplier && `· ${part.supplier}`}
                                  {part.unit_price != null && ` · $${Number(part.unit_price).toFixed(2)} ea`}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {part.unavailable && (
                                <Badge variant="destructive">Unavailable</Badge>
                              )}
                              {!part.ordered && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleUpdatePart(part.part_id, { unavailable: !part.unavailable })}
                                >
                                  {part.unavailable ? 'Mark Available' : 'Mark Unavailable'}
                                </Button>
                              )}
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeletePart(part.part_id)}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  {/* Tools Tab */}
                  <TabsContent value="tools" className="space-y-4">
                    <div className="flex justify-between items-center gap-2 flex-wrap">
                      <h3 className="font-medium">Tools Needed</h3>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleGetAISuggestion('tools')}
                          disabled={aiLoading}
                        >
                          <Sparkles className="mr-2 h-4 w-4" />
                          AI Suggest
                        </Button>
                        <Button size="sm" onClick={() => setShowToolDialog(true)}>
                          <Plus className="mr-2 h-4 w-4" />
                          Add Tool
                        </Button>
                      </div>
                    </div>
                    
                    {/* AI Tools Suggestions inline */}
                    {aiSuggestions?.type === 'tools' && parsedAiSuggestions.length > 0 && (
                      <Card className="border-indigo-200 bg-indigo-50/50">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <Sparkles className="h-4 w-4 text-indigo-500" />
                              AI Suggested Tools
                            </CardTitle>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setAiSuggestions(null);
                                setParsedAiSuggestions([]);
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {parsedAiSuggestions.map((tool, idx) => {
                            const toolName = typeof tool === 'string' ? tool : tool.name;
                            return (
                              <div 
                                key={idx}
                                className="flex items-center justify-between p-2 bg-white rounded-lg"
                              >
                                <div className="flex items-center gap-2">
                                  <Wrench className="h-4 w-4 text-slate-500" />
                                  <span className="font-medium">{toolName}</span>
                                </div>
                                <Button 
                                  size="sm" 
                                  onClick={() => handleAddAITool(toolName)}
                                >
                                  <PlusCircle className="mr-1 h-4 w-4" />
                                  Add
                                </Button>
                              </div>
                            );
                          })}
                        </CardContent>
                      </Card>
                    )}
                    
                    {selectedTask.tools?.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Wrench className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                        <p>No tools added yet</p>
                        <p className="text-sm mt-1">Use "AI Suggest" to get recommendations</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {selectedTask.tools?.map(tool => (
                          <div 
                            key={tool.tool_id}
                            className="flex items-center justify-between p-3 border rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <Wrench className="h-4 w-4 text-slate-500" />
                              <span className="font-medium">{tool.name}</span>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeleteTool(tool.tool_id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="attachments" className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="font-medium">Attachments</h3>
                      <label>
                        <input
                          type="file"
                          className="hidden"
                          onChange={handleFileUpload}
                        />
                        <Button size="sm" asChild>
                          <span>
                            <Plus className="mr-2 h-4 w-4" />
                            Upload File
                          </span>
                        </Button>
                      </label>
                    </div>
                    {selectedTask.attachments?.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Paperclip className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                        <p>No attachments yet</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {selectedTask.attachments?.map(att => (
                          <div 
                            key={att.attachment_id}
                            className="border rounded-lg overflow-hidden group"
                          >
                            {isImageFile(att.filetype) ? (
                              <div 
                                className="aspect-video bg-slate-100 relative cursor-pointer"
                                onClick={() => setPreviewImage(tasksAPI.getAttachmentUrl(selectedTask.task_id, att.attachment_id))}
                              >
                                <img
                                  src={tasksAPI.getAttachmentUrl(selectedTask.task_id, att.attachment_id)}
                                  alt={att.filename}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                  <Eye className="h-8 w-8 text-white" />
                                </div>
                              </div>
                            ) : (
                              <div className="aspect-video bg-slate-100 flex items-center justify-center">
                                <FileText className="h-12 w-12 text-slate-400" />
                              </div>
                            )}
                            <div className="p-3">
                              <p className="font-medium text-sm truncate">{att.filename}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <a
                                  href={tasksAPI.getAttachmentUrl(selectedTask.task_id, att.attachment_id)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-indigo-600 hover:underline text-sm"
                                >
                                  Download
                                </a>
                                <button
                                  onClick={() => handleDeleteAttachment(att.attachment_id)}
                                  className="text-red-600 hover:underline text-sm"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="ai" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Button 
                        variant="outline" 
                        onClick={() => handleGetAISuggestion('parts')}
                        disabled={aiLoading}
                        className="h-auto py-3"
                      >
                        <div className="flex flex-col items-center gap-1">
                          <Package className="h-5 w-5" />
                          <span>Suggest Parts</span>
                        </div>
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => handleGetAISuggestion('tools')}
                        disabled={aiLoading}
                        className="h-auto py-3"
                      >
                        <div className="flex flex-col items-center gap-1">
                          <Wrench className="h-5 w-5" />
                          <span>Suggest Tools</span>
                        </div>
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => handleGetAISuggestion('breakdown')}
                        disabled={aiLoading}
                        className="h-auto py-3"
                      >
                        <div className="flex flex-col items-center gap-1">
                          <ListChecks className="h-5 w-5" />
                          <span>Task Breakdown</span>
                        </div>
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => handleGetAISuggestion('tips')}
                        disabled={aiLoading}
                        className="h-auto py-3"
                      >
                        <div className="flex flex-col items-center gap-1">
                          <Lightbulb className="h-5 w-5" />
                          <span>Tips & Tricks</span>
                        </div>
                      </Button>
                    </div>
                    
                    {/* Request Quote Button */}
                    <Button 
                      className="w-full bg-amber-500 hover:bg-amber-600 text-white h-auto py-3"
                      onClick={handleRequestQuote}
                      disabled={quoteLoading}
                      data-testid="request-quote-btn"
                    >
                      <Receipt className="mr-2 h-5 w-5" />
                      {quoteLoading ? 'Generating Quote...' : 'Request Quote'}
                    </Button>

                    {/* Quote Loading */}
                    {quoteLoading && (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500"></div>
                        <span className="ml-3 text-slate-600">Generating quote...</span>
                      </div>
                    )}

                    {/* Quote Display */}
                    {quoteData && (
                      <Card className="border-amber-200 bg-amber-50/50">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <Receipt className="h-4 w-4 text-amber-600" />
                              Job Quote
                            </CardTitle>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={copyQuote} data-testid="copy-quote-btn">
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setQuoteData(null)}>
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <p className="text-slate-700 text-sm leading-relaxed">{quoteData.summary}</p>
                          <div className="border-t border-amber-200 pt-3 space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Materials</span>
                              <span className="font-medium">{quoteData.materials_estimate}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Labour ({quoteData.labour_hours} hrs)</span>
                              <span className="font-medium">{quoteData.labour_estimate}</span>
                            </div>
                            <div className="flex justify-between text-base border-t border-amber-200 pt-2 mt-2">
                              <span className="font-semibold">Total Estimate</span>
                              <span className="font-bold text-amber-600">{quoteData.total_estimate}</span>
                            </div>
                          </div>
                          {quoteData.notes && (
                            <p className="text-xs text-slate-500 italic">{quoteData.notes}</p>
                          )}
                          <Button
                            className="w-full mt-2 bg-indigo-600 hover:bg-indigo-700 text-white"
                            onClick={openQuoteEditor}
                            data-testid="save-as-quote-btn"
                          >
                            <Receipt className="mr-2 h-4 w-4" />
                            Save as Quote
                          </Button>
                        </CardContent>
                      </Card>
                    )}

                    {aiLoading && (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        <span className="ml-3 text-slate-600">Getting AI suggestions...</span>
                      </div>
                    )}
                    
                    {aiSuggestions && (
                      <Card>
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-lg capitalize flex items-center gap-2">
                              <Sparkles className="h-5 w-5 text-indigo-500" />
                              {aiSuggestions.type === 'tips' ? 'Tips & Tricks' : aiSuggestions.type} Suggestions
                            </CardTitle>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setAiSuggestions(null);
                                setParsedAiSuggestions([]);
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {/* Parts suggestions with Add buttons */}
                          {aiSuggestions.type === 'parts' && parsedAiSuggestions.length > 0 && (
                            <div className="space-y-2">
                              {parsedAiSuggestions.map((part, idx) => (
                                <div 
                                  key={idx}
                                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                                >
                                  <div>
                                    <span className="font-medium">{part.name}</span>
                                    <span className="text-slate-500 ml-2">
                                      Qty: {part.quantity || 1}
                                      {part.supplier && ` · ${part.supplier}`}
                                      {part.unit_price != null && ` · $${Number(part.unit_price).toFixed(2)}`}
                                    </span>
                                  </div>
                                  <Button 
                                    size="sm" 
                                    onClick={() => handleAddAIPart(part)}
                                    className="bg-indigo-600 hover:bg-indigo-700"
                                  >
                                    <PlusCircle className="mr-1 h-4 w-4" />
                                    Add
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Tools suggestions with Add buttons */}
                          {aiSuggestions.type === 'tools' && parsedAiSuggestions.length > 0 && (
                            <div className="space-y-2">
                              {parsedAiSuggestions.map((tool, idx) => {
                                const toolName = typeof tool === 'string' ? tool : tool.name;
                                return (
                                  <div 
                                    key={idx}
                                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                                  >
                                    <div className="flex items-center gap-2">
                                      <Wrench className="h-4 w-4 text-slate-500" />
                                      <span className="font-medium">{toolName}</span>
                                    </div>
                                    <Button 
                                      size="sm" 
                                      onClick={() => handleAddAITool(toolName)}
                                      className="bg-indigo-600 hover:bg-indigo-700"
                                    >
                                      <PlusCircle className="mr-1 h-4 w-4" />
                                      Add
                                    </Button>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Breakdown with Add to Description button */}
                          {aiSuggestions.type === 'breakdown' && parsedAiSuggestions.length > 0 && (
                            <div className="space-y-3">
                              <div className="space-y-2 max-h-60 overflow-y-auto">
                                {parsedAiSuggestions.map((item, idx) => (
                                  <div 
                                    key={idx}
                                    className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg"
                                  >
                                    <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-sm font-medium">
                                      {idx + 1}
                                    </span>
                                    <span>{typeof item === 'string' ? item : item.title || item.name || JSON.stringify(item)}</span>
                                  </div>
                                ))}
                              </div>
                              <Button 
                                onClick={handleAddBreakdownToDescription}
                                className="w-full bg-indigo-600 hover:bg-indigo-700"
                              >
                                <PlusCircle className="mr-2 h-4 w-4" />
                                Add Breakdown to Description
                              </Button>
                            </div>
                          )}

                          {/* Tips with Add to Description button */}
                          {aiSuggestions.type === 'tips' && parsedAiSuggestions.length > 0 && (
                            <div className="space-y-3">
                              <div className="space-y-2 max-h-60 overflow-y-auto">
                                {parsedAiSuggestions.map((tip, idx) => (
                                  <div 
                                    key={idx}
                                    className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg"
                                  >
                                    <Lightbulb className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                                    <span>{typeof tip === 'string' ? tip : JSON.stringify(tip)}</span>
                                  </div>
                                ))}
                              </div>
                              <Button 
                                onClick={handleAddTipsToDescription}
                                className="w-full bg-amber-500 hover:bg-amber-600"
                              >
                                <PlusCircle className="mr-2 h-4 w-4" />
                                Add Tips to Description
                              </Button>
                            </div>
                          )}

                          {/* Fallback for unparsed results */}
                          {parsedAiSuggestions.length === 0 && (
                            <div className="bg-slate-50 p-4 rounded-lg">
                              <pre className="whitespace-pre-wrap text-sm overflow-auto max-h-64">
                                {aiSuggestions.result}
                              </pre>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </TabsContent>

                  {/* Time Tracking Tab */}
                  <TabsContent value="time" className="space-y-4" data-testid="task-time-content">
                    {/* Check-in / Check-out Controls */}
                    <Card>
                      <CardContent className="p-4">
                        {activeTimeEntry && activeTimeEntry.task_id === selectedTask.task_id ? (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                              <div>
                                <p className="font-medium text-sm">Checked in</p>
                                <p className="text-xs text-slate-500">Since {format(new Date(activeTimeEntry.check_in), 'p')}</p>
                              </div>
                            </div>
                            <Button onClick={handleCheckOut} variant="destructive" size="sm" data-testid="checkout-btn">
                              <Square className="mr-2 h-4 w-4" />
                              Check Out
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm">Time Tracking</p>
                              {activeTimeEntry ? (
                                <p className="text-xs text-amber-600">Currently checked into another task</p>
                              ) : (
                                <p className="text-xs text-slate-500">Start tracking time on this task</p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button onClick={() => handleCheckIn(selectedTask.task_id)} size="sm" data-testid="checkin-btn">
                                <Play className="mr-2 h-4 w-4" />
                                Check In
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => { setManualTimeForm({ duration_minutes: '', note: '', date: new Date().toISOString().slice(0, 10) }); setShowManualTimeDialog(true); }} data-testid="manual-time-btn">
                                <Timer className="mr-2 h-4 w-4" />
                                Add Manual
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Time summary */}
                    {taskTimeEntries && (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium">
                            Total: {taskTimeEntries.total_minutes >= 60 
                              ? `${Math.floor(taskTimeEntries.total_minutes / 60)}h ${Math.round(taskTimeEntries.total_minutes % 60)}m`
                              : `${Math.round(taskTimeEntries.total_minutes)}m`
                            }
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 pt-0">
                          {taskTimeEntries.entries?.length === 0 ? (
                            <p className="text-sm text-slate-500 text-center py-4">No time entries yet</p>
                          ) : (
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {taskTimeEntries.entries?.map((entry) => (
                                <div key={entry.entry_id} className="flex items-center justify-between p-2 rounded-md bg-slate-50 text-sm" data-testid={`time-entry-${entry.entry_id}`}>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">
                                        {entry.duration_minutes != null 
                                          ? (entry.duration_minutes >= 60 
                                            ? `${Math.floor(entry.duration_minutes / 60)}h ${Math.round(entry.duration_minutes % 60)}m` 
                                            : `${Math.round(entry.duration_minutes)}m`)
                                          : 'In progress'}
                                      </span>
                                      {entry.is_manual && <Badge variant="outline" className="text-xs">Manual</Badge>}
                                      {entry.auto_closed && <Badge variant="outline" className="text-xs text-amber-600">Auto-closed</Badge>}
                                    </div>
                                    <p className="text-xs text-slate-500">
                                      {format(new Date(entry.check_in), 'PP')}
                                      {entry.note && ` — ${entry.note}`}
                                    </p>
                                  </div>
                                  <Button variant="ghost" size="sm" onClick={() => handleDeleteTimeEntry(entry.entry_id)} className="h-7 w-7 p-0">
                                    <Trash2 className="h-3.5 w-3.5 text-red-400" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </TabsContent>

                  {/* Quotes Tab */}
                  <TabsContent value="quotes" className="space-y-4" data-testid="task-quotes-content">
                    {taskQuotes.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Receipt className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                        <p>No quotes for this task</p>
                        <p className="text-xs mt-1">Use the AI Assist tab to generate and save a quote</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {taskQuotes.map((q) => (
                          <Card key={q.quote_id} data-testid={`task-quote-${q.quote_id}`}>
                            <CardContent className="p-3">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-medium text-sm truncate">{q.title}</h4>
                                <Badge className={
                                  q.status === 'DRAFT' ? 'bg-slate-100 text-slate-700' :
                                  q.status === 'SENT' ? 'bg-blue-100 text-blue-700' :
                                  q.status === 'ACCEPTED' ? 'bg-emerald-100 text-emerald-700' :
                                  q.status === 'REJECTED' ? 'bg-red-100 text-red-700' :
                                  'bg-amber-100 text-amber-700'
                                }>{q.status}</Badge>
                              </div>
                              {q.summary && <p className="text-xs text-slate-500 mb-2 line-clamp-2">{q.summary}</p>}
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-slate-400">{format(new Date(q.created_at), 'PP')} · {q.line_items?.length || 0} items</span>
                                <div className="flex items-center gap-2">
                                  <div className="text-right">
                                    <span className="font-bold text-sm">${(q.total || 0).toFixed(2)}</span>
                                    {q.gst_registered && q.gst_amount > 0 && (
                                      <p className="text-[10px] text-slate-500">{q.gst_inclusive ? 'inc.' : '+'} ${q.gst_amount.toFixed(2)} GST</p>
                                    )}
                                  </div>
                                  <Button
                                    variant="ghost" size="sm" className="h-7 w-7 p-0"
                                    onClick={async () => {
                                      try {
                                        const res = await quotesAPI.downloadPdf(q.quote_id);
                                        const blob = new Blob([res.data], { type: 'application/pdf' });
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = `${q.title || 'quote'}.pdf`;
                                        a.click();
                                        URL.revokeObjectURL(url);
                                      } catch { toast.error('Failed to download PDF'); }
                                    }}
                                    data-testid={`quote-pdf-${q.quote_id}`}
                                  >
                                    <Download className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="activity" className="space-y-4">
                    {selectedTask.audit_log?.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Clock className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                        <p>No activity yet</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {selectedTask.audit_log?.slice().reverse().map(entry => (
                          <div 
                            key={entry.entry_id}
                            className="flex items-start gap-3 pb-4 border-b border-slate-100 last:border-0"
                          >
                            <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-indigo-600 text-xs font-medium">
                                {entry.user_name?.charAt(0) || 'U'}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm">
                                <span className="font-medium">{entry.user_name}</span>
                                <span className="text-slate-500"> {entry.action}</span>
                              </p>
                              {entry.details && (
                                <p className="text-sm text-slate-600 mt-1">{entry.details}</p>
                              )}
                              <p className="text-xs text-slate-400 mt-1">
                                {format(new Date(entry.timestamp), 'PPp')}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </div>

              <DialogFooter className="border-t pt-4 flex-shrink-0">
                <Button variant="outline" onClick={() => handleEditTask(selectedTask)}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit Task
                </Button>
                <Button variant="destructive" onClick={() => setDeleteTaskId(selectedTask.task_id)}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Part Dialog */}
      <Dialog open={showPartDialog} onOpenChange={setShowPartDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Part</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>Part Name *</Label>
              <Input
                value={partForm.name}
                onChange={(e) => setPartForm({ ...partForm, name: e.target.value })}
                placeholder="Part name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={partForm.quantity}
                  onChange={(e) => setPartForm({ ...partForm, quantity: parseInt(e.target.value) || 1 })}
                  min="1"
                />
              </div>
              <div>
                <Label>Supplier</Label>
                <Input
                  value={partForm.supplier}
                  onChange={(e) => setPartForm({ ...partForm, supplier: e.target.value })}
                  placeholder="Supplier name"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPartDialog(false)}>Cancel</Button>
            <Button onClick={handleAddPart}>Add Part</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Image Preview</DialogTitle>
          </DialogHeader>
          {previewImage && (
            <img src={previewImage} alt="Preview" className="w-full h-auto max-h-[70vh] object-contain" />
          )}
        </DialogContent>
      </Dialog>

      {/* Add Tool Dialog */}
      <Dialog open={showToolDialog} onOpenChange={setShowToolDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Tool</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Tool Name *</Label>
            <Input
              value={toolName}
              onChange={(e) => setToolName(e.target.value)}
              placeholder="e.g., Screwdriver, Drill, Wrench"
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowToolDialog(false)}>Cancel</Button>
            <Button onClick={handleAddTool}>Add Tool</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Photo Job Wizard */}
      <PhotoJobWizard
        open={showPhotoWizard}
        onClose={() => setShowPhotoWizard(false)}
        onTaskCreated={() => { fetchTasks(); }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTaskId} onOpenChange={(open) => { if (!open) setDeleteTaskId(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Task</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this task? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTaskId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDeleteTask(deleteTaskId)} data-testid="confirm-delete-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Time Entry Dialog */}
      <Dialog open={showManualTimeDialog} onOpenChange={setShowManualTimeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Manual Time Entry</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>Duration (minutes) *</Label>
              <Input
                type="number"
                value={manualTimeForm.duration_minutes}
                onChange={(e) => setManualTimeForm({ ...manualTimeForm, duration_minutes: e.target.value })}
                placeholder="e.g. 90"
                min="1"
                data-testid="manual-time-duration"
              />
            </div>
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={manualTimeForm.date}
                onChange={(e) => setManualTimeForm({ ...manualTimeForm, date: e.target.value })}
                data-testid="manual-time-date"
              />
            </div>
            <div>
              <Label>Note</Label>
              <Input
                value={manualTimeForm.note}
                onChange={(e) => setManualTimeForm({ ...manualTimeForm, note: e.target.value })}
                placeholder="Optional note"
                data-testid="manual-time-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualTimeDialog(false)}>Cancel</Button>
            <Button onClick={handleAddManualTime} data-testid="save-manual-time-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quote Editor Dialog */}
      <Dialog open={showQuoteEditor} onOpenChange={setShowQuoteEditor}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Save Quote</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Title</Label>
              <Input value={quoteForm.title} onChange={(e) => setQuoteForm({ ...quoteForm, title: e.target.value })} className="mt-1" data-testid="quote-editor-title" />
            </div>
            <div>
              <Label>Summary</Label>
              <Textarea value={quoteForm.summary} onChange={(e) => setQuoteForm({ ...quoteForm, summary: e.target.value })} rows={2} className="mt-1" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Line Items</Label>
                <Button variant="outline" size="sm" onClick={() => setQuoteForm({ ...quoteForm, line_items: [...quoteForm.line_items, { description: '', quantity: 1, unit_price: 0 }] })} data-testid="quote-add-line-item">
                  <PlusCircle className="mr-1 h-3 w-3" /> Add Item
                </Button>
              </div>
              {quoteForm.line_items.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-3">No line items</p>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-12 gap-2 text-xs text-slate-500 font-medium px-1">
                    <span className="col-span-5">Description</span>
                    <span className="col-span-2">Qty</span>
                    <span className="col-span-2">Unit $</span>
                    <span className="col-span-2 text-right">Total</span>
                    <span className="col-span-1"></span>
                  </div>
                  {quoteForm.line_items.map((li, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`quote-line-${idx}`}>
                      <Input className="col-span-5 h-8 text-sm" value={li.description} onChange={(e) => updateQuoteFormItem(idx, 'description', e.target.value)} />
                      <Input className="col-span-2 h-8 text-sm" type="number" min="0" value={li.quantity} onChange={(e) => updateQuoteFormItem(idx, 'quantity', e.target.value)} />
                      <Input className="col-span-2 h-8 text-sm" type="number" min="0" step="0.01" value={li.unit_price} onChange={(e) => updateQuoteFormItem(idx, 'unit_price', e.target.value)} />
                      <span className="col-span-2 text-sm font-medium text-right">${((parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0)).toFixed(2)}</span>
                      <Button variant="ghost" size="sm" className="col-span-1 h-8 w-8 p-0" onClick={() => setQuoteForm({ ...quoteForm, line_items: quoteForm.line_items.filter((_, i) => i !== idx) })}>
                        <Trash2 className="h-3.5 w-3.5 text-red-400" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex justify-end pt-2 border-t">
                    <span className="text-base font-bold">Total: ${quoteFormSubtotal.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
            <div>
              <Label>Notes / Terms</Label>
              <Textarea value={quoteForm.notes} onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })} rows={3} className="mt-1" placeholder="Payment terms, conditions, etc." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuoteEditor(false)}>Cancel</Button>
            <Button onClick={handleSaveQuote} data-testid="save-quote-btn">Save Quote</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TasksPage;
